import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { concatMap, delay, retryWhen, tap } from 'rxjs/operators';
import { defer, Observable, of, throwError } from 'rxjs';

@Injectable()
export class RetryHttpInterceptor implements HttpInterceptor {

  constructor() {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // The crew opportunity photo-upload POST must not be retried (a retry can create duplicate uploads). This covers both the
    // legacy main-API route (`opportunities/{id}/files/crew-app`) and the migrated Crew API route (`crew-app/opportunities/{id}/files`).
    const isCrewPhotoUploadPost = req.method === 'POST'
        && (req.url.endsWith('files/crew-app') || (req.url.includes('/crew-app/opportunities/') && req.url.endsWith('/files')));

    // Assigning inventory items to a container skips this interceptor to avoid stacking a second retry layer
    // on the one ApiClientService.put already applies, which for PUT has no opt-out. This is not what keeps
    // duplicate history rows out, and should not be read as though it were: the endpoint itself passes over
    // items already in the target container, so a retry landing after the original committed writes no audit
    // rows. Fewer attempts here only narrows the window where a retry overlaps an original still in flight.
    const isContainerAssignPut = req.method === 'PUT' && req.url.endsWith('/inventory/container');

    // Each storage-item add-damage POST creates a record, so a retry after a lost response would save a duplicate.
    const isStorageDamagePost = req.method === 'POST'
        && req.url.includes('/crew-app/warehouse/storage-accounts/') && req.url.endsWith('/damages');

    // Receiving an item (and attaching its photos) has no idempotency key, so a replay after a lost response
    // would record a second item with its own number.
    const isReceiveItemPost = req.method === 'POST' && req.url.includes('/crew-app/warehouse/work-orders/') && req.url.includes('/receive/items');

    // Raising and discarding a receive's work order from Tasks are sent once, like the receive's other writes.
    const isQuickReceivePost = req.method === 'POST' && req.url.includes('/crew-app/warehouse/')
        && (req.url.endsWith('/receive/work-order') || req.url.endsWith('/receive/discard'));

    if (req.url.includes('/ping') || isCrewPhotoUploadPost || isContainerAssignPut || isStorageDamagePost || isReceiveItemPost
        || isQuickReceivePost) {
      return next.handle(req);
    }
    return next.handle(req).pipe(retryWhen(x => this.standardRetryPolicy(x)));
  }

  private standardRetryPolicy(errors: Observable<any>) {
    return errors.pipe(
        tap(e => console.log('EXECUTING RETRY: ', e)),
        concatMap((e, i) =>
            defer(() => i > 5 || e.status ? throwError(e) : of(e).pipe(delay(1000)))
        )
    );
  }
}
